import {
    ApplicationHelpType,
    EmptyGuid,
    NoteAcknowledgementStatusTypesEnum,
    NoteApprovalStatusTypesEnum,
    NotePurposeTypesEnum,
    NoteTypes,
    UserRightsEnum,
} from '../../../utilities/Constants';
import {
    IAlertUser,
    INoteCategoryModel,
    INoteCategoryTypeModel,
    INoteModel,
    INotesUICustomization,
} from '../../../interfaces/INote';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import React, { JSX } from 'react';

import Authorization from '../../../stores/Authorization';
import CaseHelper from '../../../utilities/CaseHelper';
import CheckmateSelect from '../../shared/CheckmateSelect';
import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import { Help } from '../../shared/Help';
import { IEntityAssertionModel } from '../../../interfaces/IAssertionDescriptor';
import { IUserModel } from '../../../interfaces/IUser';
import { IValidation } from '../../../interfaces/IError';
import NoteHelper from '../../../utilities/NoteHelper';
import { Stack } from 'react-bootstrap';
import { SubCategories } from './NoteEditorSubCategories';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

interface INoteEditorFields {
    acknowledgmentStatusTypes: ILookupModel[];
    allAlertTargets: IMultiSelectOption[];
    approvalStatusTypes: ILookupModel[];
    currentNote: INoteModel;
    enableNoteAssociations?: boolean;
    entityItems?: IMultiSelectOption[];
    forceNoteEditorInReadOnlyMode?: boolean;
    getAdditionalFieldsByNoteCategory: (
        noteCategory: INoteCategoryModel
    ) => JSX.Element | undefined;
    getUserFullNameDisplay: (user: IUserModel) => string;
    isQuadNoteEditor?: boolean;
    onChangeValue: (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLTextAreaElement>
            | React.ChangeEvent<HTMLSelectElement>
    ) => void;
    onCheckChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onNoteAssociationSelected: (optionsList: any) => void;
    onNoteCategoriesSelected: (optionsList: any) => void;
    onNoteCategoryAdditionalFieldChange: (items: IEntityAssertionModel[], key?: any) => void;
    onNoteOrganizationsSelected: (optionsList: any) => void;
    onSearchableSingleSelectChange: (selectedItem: any, identifier: string) => void;
    noteAssociationTitle?: string;
    noteCategoryTypes: INoteCategoryTypeModel[];
    notePurposeTypes: ILookupModel[];
    notesUICustomization?: INotesUICustomization;
    returnToPriorTarget: boolean;
    selectedAlertTarget: IMultiSelectOption;
    selectedEntityItems?: IMultiSelectOption[];
    selectedNoteCategories: IMultiSelectOption[];
    selectedOrganizations?: IMultiSelectOption[];
    validation: IValidation;
}

export function NoteEditorFields(props: INoteEditorFields) {
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();

    const getIsLocalBasicUser = (user: IUserModel) => {
        return Authorization.isLocalScope(user, zoneContext.zoneGuid);
    };
    //current Dbnote's purpose  is either Ack or Approve.
    const isDbNoteHasAckOrApprove = props.currentNote.alert?.guid;

    const getNotePurposesByNoteType = () => {
        if (
            props.currentNote.type?.id === NoteTypes.Personal ||
            props.currentNote.type?.id === NoteTypes.StrategySignificantChange ||
            //// TODO: DJS - For now limit quad zone notes to Info purpose only. Will revisit if other purposes are needed.
            (props.currentNote.type?.id === NoteTypes.ZoneNote && props.isQuadNoteEditor)
        ) {
            return props.notePurposeTypes.filter((x) => x.id === NotePurposeTypesEnum.Info);
        }

        if (
            isDbNoteHasAckOrApprove &&
            (props.currentNote.purpose?.id === NotePurposeTypesEnum.Approval ||
                props.currentNote.purpose?.id === NotePurposeTypesEnum.Acknowledgement)
        ) {
            return props.notePurposeTypes.filter((x) => x.id !== NotePurposeTypesEnum.Info);
        }
        return props.notePurposeTypes;
    };

    const getContentLength = (): number => props.currentNote.content?.length ?? 0;
    const getContextLength = (): number => props.currentNote.context?.length ?? 0;

    const { guid, purpose } = props.currentNote;
    const isGuidValid = guid && guid !== EmptyGuid;
    const isInfoPurpose = purpose.id === NotePurposeTypesEnum.Info;
    const isLocalBasicUser = getIsLocalBasicUser(user);
    const displayPurposeAsLabel = isGuidValid && isInfoPurpose && isLocalBasicUser;
    const isCaseNoteAndLocalUser =
        Authorization.isLocalScope(user, zoneContext.zoneGuid) &&
        props.currentNote.type?.id === NoteTypes.CaseNote;

    const organizationsWithAddZoneNoteRight = Authorization.getUserOrganizationsWithRight(
        UserRightsEnum.AddZoneNote,
        user
    );

    const getPriorTargetUser = () => {
        const alert = props.currentNote.alert;

        const sortedHistory = [...(alert?.actionTargetHistory ?? [])].sort(
            (a, b) => (b.sortOrder ?? 0) - (a.sortOrder ?? 0)
        );

        const targetUser = sortedHistory.length > 1 ? sortedHistory[1]?.user : alert?.createdBy;

        return targetUser;
    };

    const getPriorTargetName = () => {
        const targetUser = getPriorTargetUser();

        const firstName = targetUser?.profile?.firstName;
        const lastName = targetUser?.profile?.lastName;

        return firstName ? `${firstName} ${lastName ?? ''}`.trim() : '';
    };

    const allowReturnToPriorUser = () => {
        const { currentNote } = props;
        const { purpose, acknowledgmentStatus, approvalStatus, alert } = currentNote;

        if (!alert) {
            return false;
        }

        const targetUser = getPriorTargetUser();
        if (targetUser?.guid === user.guid) {
            return false;
        }

        const isAcknowledgedOrDeclined =
            acknowledgmentStatus?.id === NoteAcknowledgementStatusTypesEnum.Acknowledge ||
            acknowledgmentStatus?.id === NoteAcknowledgementStatusTypesEnum.Decline;

        const isApprovedOrNotApproved =
            approvalStatus?.id === NoteApprovalStatusTypesEnum.Approved ||
            approvalStatus?.id === NoteApprovalStatusTypesEnum.NotApproved;

        if (
            (purpose.id === NotePurposeTypesEnum.Acknowledgement && isAcknowledgedOrDeclined) ||
            (purpose.id === NotePurposeTypesEnum.Approval && isApprovedOrNotApproved)
        ) {
            return false;
        }

        return (alert.actionTargetHistory?.length ?? 0) > 0;
    };

    return (
        <div id="NoteMainSection" className="col-sm-9">
            {props.isQuadNoteEditor && (
                <div className="row mb-2">
                    <div className="col-sm-2">
                        <label className="text-gray">Zones *</label>
                    </div>
                    <div className="col-sm-10">
                        <div className="col-sm-3">
                            <CheckmateSelect
                                isMulti={true}
                                options={CheckmateSelectHelper.getOrgOptions(
                                    organizationsWithAddZoneNoteRight
                                )}
                                value={props.selectedOrganizations}
                                onChange={props.onNoteOrganizationsSelected}
                            />
                            <span className="text-danger">
                                {props.validation.organizationGuids}
                            </span>
                        </div>
                    </div>
                </div>
            )}
            <div className="row mb-2">
                <div className="col-sm-2">
                    <label className="text-gray">Purpose *</label>
                </div>
                <div className="col-sm-10">
                    {displayPurposeAsLabel ? (
                        <div className="col-sm-3">
                            <span>{props.currentNote.purpose.displayName}</span>
                        </div>
                    ) : (
                        <div className="col-sm-3">
                            <CheckmateSelect
                                options={CheckmateSelectHelper.getLookupOptions(
                                    getNotePurposesByNoteType()
                                )}
                                value={CheckmateSelectHelper.getSelectedValueById(
                                    getNotePurposesByNoteType(),
                                    props.currentNote.purpose.id
                                )}
                                name="purpose"
                                onChange={(e: any) => {
                                    props.onSearchableSingleSelectChange(e, 'purpose');
                                }}
                                notClearable
                            />
                            <span className="text-danger">{props.validation.notePurpose}</span>
                        </div>
                    )}
                </div>
            </div>

            {props.currentNote.purpose.id === NotePurposeTypesEnum.Approval && (
                <div className="row mb-2">
                    <div className="col-sm-2">
                        <label className="text-gray">Decision</label>
                    </div>
                    <div className="col-sm-10 d-inline-block">
                        <div className="d-inline-block col-sm-3">
                            <CheckmateSelect
                                options={CheckmateSelectHelper.getLookupOptions(
                                    props.approvalStatusTypes
                                )}
                                value={CheckmateSelectHelper.getSelectedValueById(
                                    props.approvalStatusTypes,
                                    props.currentNote.approvalStatus
                                        ? props.currentNote.approvalStatus.id
                                        : 0
                                )}
                                name="approvalStatus"
                                onChange={(e: any) => {
                                    props.onSearchableSingleSelectChange(e, 'approvalStatus');
                                }}
                                isDisabled={
                                    !Authorization.userHasRight(
                                        UserRightsEnum.EditNoteApprovalStatus,
                                        user,
                                        zoneContext.zoneGuid
                                    )
                                }
                            />
                        </div>
                        <span className="btn btn-no-bg ps-3">
                            <label>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={props.currentNote.modifiedBeforeApproval || false}
                                    disabled={
                                        !Authorization.userHasRight(
                                            UserRightsEnum.EditNoteApprovalStatus,
                                            user,
                                            zoneContext.zoneGuid
                                        )
                                    }
                                    name="chkModifiedBeforeApproval"
                                    onChange={props.onCheckChanged}
                                />
                                <span className="ms-2 text-gray">Approved with Modification</span>
                            </label>
                            <Help
                                type={ApplicationHelpType.Info}
                                title="Approved with Modification"
                                helpText="The user providing their approval should check this box if their approval is contingent upon modifications to the original request. "
                            />
                        </span>

                        <span className="text-danger">{props.validation.approvalStatus}</span>
                    </div>
                </div>
            )}

            {props.currentNote.purpose.id === NotePurposeTypesEnum.Acknowledgement && (
                <div className="row mb-2">
                    <div className="col-sm-2">
                        <label className="text-gray">Decision</label>
                    </div>
                    <div className="col-sm-10 d-inline-block">
                        <div className="col-sm-3 d-inline-block">
                            <CheckmateSelect
                                options={CheckmateSelectHelper.getLookupOptions(
                                    props.acknowledgmentStatusTypes
                                )}
                                value={CheckmateSelectHelper.getSelectedValueById(
                                    props.acknowledgmentStatusTypes,
                                    props.currentNote.acknowledgmentStatus
                                        ? props.currentNote.acknowledgmentStatus.id
                                        : 0
                                )}
                                name="acknowledgmentStatus"
                                onChange={(e: any) => {
                                    props.onSearchableSingleSelectChange(e, 'acknowledgmentStatus');
                                }}
                                isDisabled={
                                    Authorization.isLocalScope(user) &&
                                    props.selectedAlertTarget.guid !== user.guid
                                }
                            />
                        </div>
                        <span className="text-danger">{props.validation.acknowledgmentStatus}</span>
                    </div>
                </div>
            )}

            <div className="row mb-2">
                <div className="col-sm-2">
                    <label className="text-gray">Category *</label>
                </div>
                <div className="col-sm-10">
                    <div className="row">
                        <div className="col-sm-11">
                            <CheckmateSelect
                                isMulti={true}
                                options={NoteHelper.getNoteCategoryTypeOptionsForMultiselect(
                                    props.noteCategoryTypes
                                )}
                                value={props.selectedNoteCategories}
                                onChange={props.onNoteCategoriesSelected}
                            />
                        </div>
                        <div className="col-sm-1 pt-2">
                            {props.selectedNoteCategories.length > 1 && (
                                <Help
                                    type={ApplicationHelpType.Check}
                                    title={'Selected Categories'}
                                    helpText={CaseHelper.getMultiSelectSelectedValuesTitleText(
                                        props.selectedNoteCategories
                                    )}
                                />
                            )}
                        </div>
                    </div>
                    <span className="text-danger col-sm-3">{props.validation.categories}</span>
                </div>
            </div>

            <SubCategories
                currentNote={props.currentNote}
                noteCategoryTypes={props.noteCategoryTypes}
                onSubCategoryChange={(optionsList: any): void => {
                    props.onSearchableSingleSelectChange(optionsList, 'subCategory');
                }}
                selectedNoteCategories={props.selectedNoteCategories}
                validation={props.validation}
            />

            {props.currentNote.categories && (
                <div>
                    {props.currentNote.categories.map((item: INoteCategoryModel, i: number) => {
                        const currentError = props.validation.additionalFields
                            ?.map((field) => {
                                const parts = field.split('|');

                                return {
                                    id: parts[0],
                                    message: parts[1],
                                };
                            })
                            ?.find((error) => error.id === item.id.toString());

                        return (
                            <div key={i}>
                                {currentError && (
                                    <span className="text-danger">{currentError.message}</span>
                                )}
                                {props.getAdditionalFieldsByNoteCategory(item)}
                            </div>
                        );
                    })}
                </div>
            )}
            {props.enableNoteAssociations && (
                <div className="form-group">
                    <label className="text-gray">
                        {props.noteAssociationTitle || 'Association(s)'}
                    </label>
                    <span className="text-danger">&nbsp;{props.validation.associations}</span>
                    <div
                        className="mb-2 "
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ flexGrow: 1 }}>
                            <CheckmateSelect
                                isMulti={true}
                                options={props.entityItems || []}
                                value={props.selectedEntityItems}
                                onChange={props.onNoteAssociationSelected}
                            />
                        </div>
                        <div>
                            {(props.selectedEntityItems?.length ?? 0) > 1 && (
                                <Help
                                    type={ApplicationHelpType.Check}
                                    title="Selected Values"
                                    helpText={NoteHelper.getSelectedValuesTextFromMultiSelect(
                                        props.selectedEntityItems ?? []
                                    )}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div>
                {props.currentNote.alert && (
                    <div className="row mb-2">
                        <div className="col-sm-2">
                            <label className="text-gray">Action Target *</label>
                        </div>
                        <div className="col-sm-10">
                            <Stack direction="horizontal" gap={3}>
                                <div className="col-sm-4">
                                    {isCaseNoteAndLocalUser ? (
                                        <>{props.selectedAlertTarget.label}</>
                                    ) : (
                                        <>
                                            <CheckmateSelect
                                                options={props.allAlertTargets}
                                                name="actionTarget"
                                                value={[props.selectedAlertTarget]}
                                                onChange={(e: any) => {
                                                    props.onSearchableSingleSelectChange(
                                                        e,
                                                        'actionTarget'
                                                    );
                                                }}
                                                isDisabled={props.returnToPriorTarget}
                                            />
                                            <span className="text-danger">
                                                {props.validation.actionTarget}
                                            </span>
                                        </>
                                    )}
                                </div>
                                <div className="col-sm-4">
                                    {allowReturnToPriorUser() && (
                                        <label>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={props.returnToPriorTarget || false}
                                                name="chkReturnToPriorTarget"
                                                onChange={props.onCheckChanged}
                                            />
                                            <span className="text-gray ms-2">
                                                Return to {getPriorTargetName()}
                                            </span>
                                        </label>
                                    )}
                                </div>
                            </Stack>
                        </div>
                    </div>
                )}
                <div className="mb-2">
                    <label className="text-gray">
                        Summary*&nbsp;
                        <Help
                            type={ApplicationHelpType.Info}
                            title="Summary"
                            helpText="The Summary should reflect the entire scope of the Case Note as a stand-alone statement.  The Summary will be displayed in lists throughout Checkmate, including the Case File, Note Queries and the Alert List and will also be used in routine reporting.  Additional Context can be provided and is viewable when a user opens a Note. Additional information and details can be provided in the Context box."
                        />
                    </label>
                    <span className="text-gray">
                        &nbsp;&nbsp;(
                        {1500 - getContentLength() + ' characters remaining'})
                    </span>
                    <span className="text-danger">&nbsp;{props.validation.content}</span>
                    <textarea
                        className="form-control"
                        value={
                            props.currentNote!.content
                                ? props.currentNote!.content!.replace(/\\n/gi, '\n')
                                : ''
                        }
                        name="noteContent"
                        rows={8}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            props.onChangeValue(e);
                        }}
                        maxLength={1500}
                    />
                </div>
                <div className="fmb-2">
                    <label className="text-gray">Context</label>
                    <span className="text-gray">
                        &nbsp;&nbsp;(
                        {30000 - getContextLength() + ' characters remaining'})
                    </span>
                    <span className="text-danger">&nbsp;{props.validation.context}</span>
                    <textarea
                        className="form-control"
                        value={props.currentNote?.context ?? ''}
                        name="noteContext"
                        rows={11}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                            props.onChangeValue(e);
                        }}
                        maxLength={30000}
                    />
                </div>
            </div>
        </div>
    );
}
